import {
	IListAttrs,
	IListColumnConfig,
	IListOperations,
} from '@datachain/ui-sdk/common';
import { immerable, produce } from 'immer';
import { Map, Set } from 'immutable';

import { ProjectElementsEntity } from '../project-delete-popup/project-elements.entity';
import { ProjectItemEntity } from './project-item.entity';
import { ProjectMemberEntity } from './project-member.entity';

export interface IProjectListExtra
	extends IListColumnConfig<ProjectItemEntity> {
	width?: string | number;
	filterValue?: Array<string | number>;
	filterValues?: Array<string | number>;
	sortIndex?: number;
	sortOrder?: 'asc' | 'desc';
}

interface IProjectListEntity extends IListAttrs<ProjectItemEntity> {
	availableColumns: Map<string, IProjectListExtra>;
}

const entityDefaults: IProjectListEntity = {
	elements: Map(),
	availableColumns: Map(),
};

const labelFilterFn = (searchTerm: string, item: ProjectItemEntity): boolean =>
	item.label.toLowerCase().includes(searchTerm.toLowerCase());
const descriptionFilterFn = (
	searchTerm: string,
	item: ProjectItemEntity
): boolean => item.description.toLowerCase().includes(searchTerm.toLowerCase());
const tagsFilterFn = (searchTerm: string, item: ProjectItemEntity): boolean =>
	item.tags.filter((t) =>
		t.label.toLowerCase().includes(searchTerm.toLowerCase())
	).size > 0;

export class ProjectListEntity
	implements
		IProjectListEntity,
		IListOperations<ProjectListEntity, ProjectItemEntity>
{
	private [immerable] = true;
	public elements = entityDefaults.elements;
	public availableColumns = entityDefaults.availableColumns;

	public static build(
		availableCols: Array<IProjectListExtra>
	): ProjectListEntity {
		const inst = new ProjectListEntity();
		const cols = availableCols.reduce((acc, curr) => {
			acc.push([curr.field, curr]);
			return acc;
		}, new Array<[string, IProjectListExtra]>());
		inst.availableColumns = Map(cols);
		return inst;
	}

	private constructor() {}

	public getAvailableColumns(): Map<
		string,
		IListColumnConfig<ProjectItemEntity>
	> {
		return this.availableColumns;
	}

	public setElements(elems: Map<string, ProjectItemEntity>): ProjectListEntity {
		return produce(this, (draft: ProjectListEntity) => {
			draft.elements = elems;
		});
	}

	public extractUniqTags(...startValues: Array<string>): Set<string> {
		return this.elements.reduce((acc, projectItem) => {
			if (!projectItem.tags) {
				return acc;
			}
			return Set([
				...acc.toArray(),
				...projectItem.tags
					.toList()
					.toArray()
					.map((l) => l.label),
			]);
		}, Set<string>(startValues));
	}

	public extractAccessors(...startValues: Array<string>): Set<string> {
		return this.elements.reduce((acc, projectItem) => {
			if (projectItem.accessedByAvatar && projectItem.accessedByAvatar?.name) {
				acc = acc.add(projectItem.accessedByAvatar.name);
			}
			return acc;
		}, Set<string>(startValues));
	}

	public filterProjects(term: string): ProjectListEntity {
		return produce(this, (draft: ProjectListEntity) => {
			draft.elements = this.elements.filter(
				(p) =>
					labelFilterFn(term, p) ||
					descriptionFilterFn(term, p) ||
					tagsFilterFn(term, p)
			);
		});
	}
	public setProjectMembers(
		projectId: number,
		members: Array<ProjectMemberEntity>
	): ProjectListEntity {
		const project = this.elements.get(`${projectId}`);
		if (!project) {
			return this;
		}
		const updatedProject = project.setMembers(members);
		const elements = this.elements.set(`${projectId}`, updatedProject);
		return produce(this, (draft: ProjectListEntity) => {
			draft.elements = elements;
		});
	}

	public setProjectCriticalAndElements(
		projectId: number,
		dcElements: ProjectElementsEntity,
		isCritical: boolean
	): ProjectListEntity {
		const project = this.elements.get(`${projectId}`);
		if (!project) {
			return this;
		}
		const updatedProject = project
			.setElements(dcElements)
			.setCritical(isCritical);
		const elements = this.elements.set(`${projectId}`, updatedProject);
		return produce(this, (draft: ProjectListEntity) => {
			draft.elements = elements;
		});
	}

	public setProjectDeletable(
		projectId: number,
		isDeletable: boolean
	): ProjectListEntity {
		const project = this.elements.get(`${projectId}`);
		if (!project) {
			return this;
		}
		const updatedProject = project.setDeletable(isDeletable);
		const elements = this.elements.set(`${projectId}`, updatedProject);
		return produce(this, (draft: ProjectListEntity) => {
			draft.elements = elements;
		});
	}

	public getProjectDeletable(projectId: number): boolean | undefined {
		const project = this.elements.get(`${projectId}`) as ProjectItemEntity;
		return project.isDeletable;
	}

	public resetProject(projectId: number): ProjectListEntity {
		const project = this.elements.get(`${projectId}`) as ProjectItemEntity;
		const updatedProject = project
			.setDeletable(undefined)
			.setCritical(undefined)
			.setElements(undefined);
		const elements = this.elements.set(`${projectId}`, updatedProject);
		return produce(this, (draft: ProjectListEntity) => {
			draft.elements = elements;
		});
	}
}
